import type { Resource } from '@carbonwebmail/domain'
import { may } from '@carbonwebmail/domain'
import type { ReactNode } from 'react'
import { NavLink, Outlet } from 'react-router'
import { useYourRole } from '../../ui/yourRole.js'

/**
 * Everything you configure, on one screen.
 *
 * Your profile, the organisation, the team, the provider your mail leaves
 * through, the domains it leaves from and the API keys were six entries in the
 * sidebar and six screens. They are one subject — how this account is set up —
 * and splitting them made the sidebar's longest group the one people look at
 * least, while putting "which of these six is it in?" in front of every answer.
 * The contacts board learnt the same thing about its five.
 *
 * **Sending domains came from Deliverability**, which is where it had always
 * been. It is a thing you set up once and then forget, not something you watch:
 * the screens you watch are the monitor, the suppression list and the
 * reputation, and a domain sitting among them read as a fourth.
 *
 * **Each panel keeps its own view model and loses only its own heading.** The
 * tab says which one you are on, so a second heading underneath repeated it.
 * What each panel still carries is the sentence saying what it is for, because
 * that is the part a tab name cannot hold.
 *
 * **A tab nobody can open is not shown.** Every one of these except the profile
 * refuses on the server for a member rather than an administrator, and that
 * refusal is the only one that means anything — this is the courtesy of not
 * offering a door that opens onto a message about not being allowed.
 */

/**
 * The third element is the permission the tab needs, if it needs one.
 *
 * A resource name rather than a boolean, so this list and the server are
 * reading the same table. The old `adminOnly: true` was a second opinion
 * about authorisation kept in the browser, free to disagree with the guard —
 * and when it did, somebody saw a tab that answered 403.
 */
type Tab = readonly [path: string, label: string, needs?: Resource]

/**
 * The profile first, because it is the one every member has.
 *
 * Yours, then the organisation's, then the things that decide how mail leaves,
 * then the keys — roughly how often somebody comes here for each.
 */
const TABS: readonly Tab[] = [
  ['/settings', 'Profile'],
  ['/settings/organisation', 'Organisation', 'settings'],
  ['/settings/team', 'Team', 'team'],
  // Next to the team, because the seat limit is the one people meet first.
  ['/settings/plan', 'Plan', 'plan'],
  ['/settings/provider', 'Email provider', 'provider'],
  ['/settings/domains', 'Sending domains', 'domains'],
  ['/settings/api-keys', 'API keys', 'apiKeys'],
  // monday is an import source rather than a board of its own: connecting an
  // account is a thing this organisation configures once, and the import is
  // reached from it.
  ['/settings/monday', 'monday.com', 'integrations'],
]

export function SettingsBoard() {
  const yourRole = useYourRole()

  /*
   * Shown when the tab's resource is readable, not when it is writable.
   *
   * A member can open Sending domains and see why mail is failing without
   * being able to change a record — hiding the tab would leave them unable to
   * find out, which helps nobody. The screens inside decide what is editable;
   * this only decides what exists.
   */
  const shown = TABS.filter(([, , needs]) => needs === undefined || may(yourRole, needs, 'read'))

  return (
    <>
      <header className="mb-4">
        <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Settings</h1>
      </header>

      <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-1 border-line border-b">
        {shown.map(([path, label]) => (
          <SettingsTab key={path} to={path}>
            {label}
          </SettingsTab>
        ))}
      </div>

      <Outlet />
    </>
  )
}

/**
 * `end` on every one of them, so the profile tab is not lit on all the others.
 *
 * Its path is the prefix of theirs — the board's own address is where the
 * profile lives, because the first tab being a redirect is a back button that
 * does nothing.
 */
function SettingsTab({ to, children }: { readonly to: string; readonly children: ReactNode }) {
  return (
    <NavLink
      className={({ isActive }) =>
        `-mb-px border-b-2 py-2.5 font-semibold text-[13px] ${
          isActive ? 'border-brand text-brand' : 'border-transparent text-t2 hover:text-t1'
        }`
      }
      end
      to={to}
    >
      {children}
    </NavLink>
  )
}
